/**
 * Alle Server-Endpunkte als typisierte Funktionen – die einzige Stelle,
 * an der die Web-App mit der API spricht.
 */
import {
  UPLOAD_META_HEADER,
  UPLOAD_NAME_HEADER,
  type AdminStatsDto,
  type AdminUserDto,
  type AnnotationsDto,
  type CategoryDto,
  type DocumentDto,
  type DocumentListResponse,
  type DocumentPrefsDto,
  type DocumentSharesDto,
  type HueBridgeDto,
  type HueCatalogDto,
  type HueConnectDto,
  type HueLevel,
  type HuePin,
  type HueProbeDto,
  type HueStatusDto,
  type PageType,
  type PageViewEntryDto,
  type PatchDocumentPrefs,
  type PatchUserSettings,
  type PlaylistsState,
  type PresenceDto,
  type PutPresence,
  type RescanResultDto,
  type Role,
  type SessionResponse,
  type ShareTargetDto,
  type StrokeDocumentInput,
  type UploadMeta,
  type UserSettings,
} from "@piano/shared";
import { request, uploadWithProgress } from "./client.js";

const enc = encodeURIComponent;

function base64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const api = {
  // --- Auth -----------------------------------------------------------------
  login: (email: string, password: string) =>
    request<SessionResponse>("POST", "api/auth/login", { email, password }),
  logout: () => request<void>("POST", "api/auth/logout"),
  session: () => request<SessionResponse>("GET", "api/auth/session"),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>("POST", "api/auth/password", { currentPassword, newPassword }),

  // --- Dokumente ------------------------------------------------------------
  documents: async () => (await request<DocumentListResponse>("GET", "api/documents")).documents,
  document: (id: string) => request<DocumentDto>("GET", `api/documents/${enc(id)}`),
  fileUrl: (id: string) => `api/documents/${enc(id)}/file`,
  thumbnailUrl: (doc: Pick<DocumentDto, "id" | "mtimeMs">) =>
    `api/documents/${enc(doc.id)}/thumbnail?v=${Math.floor(doc.mtimeMs)}`,
  upload: (file: File, meta: UploadMeta, onProgress: (fraction: number) => void) =>
    uploadWithProgress<DocumentDto>(
      "api/documents",
      file,
      { [UPLOAD_NAME_HEADER]: enc(file.name), [UPLOAD_META_HEADER]: base64Url(JSON.stringify(meta)) },
      onProgress,
    ),
  deleteDocument: (id: string, force = false) =>
    request<void>("DELETE", `api/documents/${enc(id)}${force ? "?force=1" : ""}`),
  prefs: (id: string) => request<DocumentPrefsDto>("GET", `api/documents/${enc(id)}/prefs`),
  patchPrefs: (id: string, patch: PatchDocumentPrefs) =>
    request<DocumentPrefsDto>("PATCH", `api/documents/${enc(id)}/prefs`, patch),
  markOpened: (id: string) => request<void>("POST", `api/documents/${enc(id)}/opened`),
  setFavorite: (id: string, favorite: boolean) =>
    request<void>(favorite ? "PUT" : "DELETE", `api/documents/${enc(id)}/favorite`),
  setDocumentCategories: (id: string, categoryIds: string[]) =>
    request<void>("PUT", `api/documents/${enc(id)}/categories`, { categoryIds }),
  setDocumentPlaylists: (id: string, playlistIds: string[]) =>
    request<void>("PUT", `api/documents/${enc(id)}/playlists`, { playlistIds }),

  // --- Teilen ---------------------------------------------------------------
  shareTargets: () => request<ShareTargetDto[]>("GET", "api/users/share-targets"),
  shares: (id: string) => request<DocumentSharesDto>("GET", `api/documents/${enc(id)}/shares`),
  setShares: (id: string, userIds: string[]) =>
    request<DocumentSharesDto>("PUT", `api/documents/${enc(id)}/shares`, { userIds }),
  leaveShare: (id: string) => request<void>("DELETE", `api/documents/${enc(id)}/shares/me`),

  // --- Kategorien -----------------------------------------------------------
  categories: () => request<CategoryDto[]>("GET", "api/categories"),
  createCategory: (input: { name: string; color?: string; icon?: string }) =>
    request<CategoryDto>("POST", "api/categories", input),
  updateCategory: (id: string, input: { name?: string; color?: string; icon?: string }) =>
    request<CategoryDto>("PATCH", `api/categories/${enc(id)}`, input),
  deleteCategory: (id: string) => request<void>("DELETE", `api/categories/${enc(id)}`),

  // --- Setlists -------------------------------------------------------------
  playlists: () => request<PlaylistsState>("GET", "api/playlists"),
  createPlaylist: (input: { name?: string; icon?: string; accentColor?: string; documentIds?: string[] }) =>
    request<PlaylistsState>("POST", "api/playlists", input),
  updatePlaylist: (id: string, input: { name?: string; icon?: string; accentColor?: string }) =>
    request<PlaylistsState>("PATCH", `api/playlists/${enc(id)}`, input),
  deletePlaylist: (id: string) => request<PlaylistsState>("DELETE", `api/playlists/${enc(id)}`),
  setActivePlaylist: (id: string) => request<PlaylistsState>("PUT", "api/playlists/active", { id }),
  addToPlaylist: (id: string, documentIds: string[], position?: number) =>
    request<PlaylistsState>("POST", `api/playlists/${enc(id)}/items`, {
      documentIds,
      ...(position === undefined ? {} : { position }),
    }),
  setPlaylistItems: (id: string, documentIds: string[], currentIndex?: number) =>
    request<PlaylistsState>("PUT", `api/playlists/${enc(id)}/items`, {
      documentIds,
      ...(currentIndex === undefined ? {} : { currentIndex }),
    }),
  removeFromPlaylist: (id: string, documentId: string) =>
    request<PlaylistsState>("DELETE", `api/playlists/${enc(id)}/items/${enc(documentId)}`),
  setCurrentIndex: (id: string, index: number) =>
    request<PlaylistsState>("PUT", `api/playlists/${enc(id)}/current`, { index }),

  // --- Annotationen ---------------------------------------------------------
  annotations: (id: string) => request<AnnotationsDto>("GET", `api/documents/${enc(id)}/annotations`),
  saveAnnotations: (id: string, document: StrokeDocumentInput, version: number, keepalive = false) =>
    request<{ version: number }>(
      "PUT",
      `api/documents/${enc(id)}/annotations`,
      { document },
      { headers: { "if-match": String(version) }, keepalive },
    ),

  // --- Einstellungen, Live-Session, Statistik -------------------------------
  settings: () => request<UserSettings>("GET", "api/me/settings"),
  patchSettings: (patch: PatchUserSettings) => request<UserSettings>("PATCH", "api/me/settings", patch),
  presence: () => request<PresenceDto[]>("GET", "api/presence"),
  putPresence: (presence: PutPresence) => request<void>("PUT", "api/presence", presence),
  clearPresence: (keepalive = false) => request<void>("DELETE", "api/presence", undefined, { keepalive }),
  pageView: (pageType: PageType) => request<void>("POST", "api/stats/pageview", { pageType }),

  // --- Philips Hue -----------------------------------------------------------
  hueStatus: () => request<HueStatusDto>("GET", "api/hue"),
  hueDiscover: () => request<HueBridgeDto[]>("POST", "api/hue/discover"),
  hueProbe: (ip: string) => request<HueProbeDto>("POST", "api/hue/probe", { ip }),
  hueConnect: (bridgeId: string, ip: string) =>
    request<HueConnectDto>("POST", "api/hue/connect", { bridgeId, ip }),
  hueDisconnect: () => request<void>("DELETE", "api/hue"),
  hueCatalog: () => request<HueCatalogDto>("GET", "api/hue/catalog"),
  hueDock: (fresh = false) => request<HueCatalogDto>("GET", `api/hue/dock${fresh ? "?fresh=1" : ""}`),
  huePins: (pins: HuePin[]) => request<HueStatusDto>("PUT", "api/hue/pins", { pins }),
  hueRecall: (sceneId: string) => request<void>("POST", `api/hue/scenes/${enc(sceneId)}/recall`),
  hueGroup: (groupId: string, level: HueLevel) =>
    request<void>("POST", `api/hue/groups/${enc(groupId)}`, level),
  hueLight: (lightId: string, level: HueLevel) =>
    request<void>("POST", `api/hue/lights/${enc(lightId)}`, level),

  // --- Verwaltung -----------------------------------------------------------
  adminUsers: () => request<AdminUserDto[]>("GET", "api/admin/users"),
  adminCreateUser: (input: { email: string; password: string; role: Role; displayName?: string }) =>
    request<AdminUserDto>("POST", "api/admin/users", input),
  adminUpdateUser: (
    id: string,
    input: { displayName?: string; role?: Role; isActive?: boolean; password?: string },
  ) => request<AdminUserDto>("PATCH", `api/admin/users/${enc(id)}`, input),
  adminDeleteUser: (id: string, transferTo?: string) =>
    request<void>(
      "DELETE",
      `api/admin/users/${enc(id)}${transferTo ? `?transferTo=${enc(transferTo)}` : ""}`,
    ),
  adminStats: () => request<AdminStatsDto>("GET", "api/admin/stats"),
  adminPageViews: (limit = 50) =>
    request<PageViewEntryDto[]>("GET", `api/admin/stats/pageviews?limit=${limit}`),
  adminRescan: () => request<RescanResultDto>("POST", "api/admin/library/rescan"),
};
